import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { browserStorage } from './browser-storage';
import states from './states';
import EventBus from './event-bus';

Vue.use(Vuex);
Vue.prototype.$http = axios;
axios.defaults.baseURL = process.env.VUE_APP_API_URL;

const token = browserStorage.localStorageGet('token');
const returnData = ({ data }) => data;
const isRequestActive = (activeRequestID) => _.filter(
  store.state.activeRequests,
  { activeRequestID },
).length > 0;

if (token) {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
}

/* Default Entities */
const defaultCall = {
  CallTypeID: undefined,
  CallType: undefined,
  CallStatusID: undefined,
  CallStatus: undefined,
  CustomerID: undefined,
  Customer: undefined,
  EmployeeID: undefined,
  Employee: undefined,
  ComputerID: undefined,
  CallProblem: '',
  CallNotes: '',
  CallHistory: '',
  CallStartUtc: '',
  CallEndUtc: '',
};

const defaultCustomer = {
  CustomerID: undefined,
  CustomerName: '',
  CustomerTypeID: undefined,
  CustomerWebsite: '',
  CustomerAddress1: '',
  CustomerAddress2: '',
  CustomerCity: '',
  CustomerState: '',
  CustomerZipCode: '',
  CustomerPhone1: '',
  CustomerPhone2: '',
  CustomerFax: '',
  CustomerEmail: '',
  CustomerNotes: '',
  BillingAddress1: '',
  BillingAddress2: '',
  BillingCity: '',
  BillingState: '',
  BillingZipCode: '',
  BillingContactEmployeeID: undefined,
  BillingContactEmployee: {
    EmployeeName: '',
    EmployeePhone1: '',
    EmployeeFax: '',
    EmployeeEmail: '',
  },
  SupervisorEmployeeID: undefined,
  SupervisorEmployee: {
    EmployeeName: '',
    EmployeePhone1: '',
    EmployeeFax: '',
    EmployeeEmail: '',
  },
  Notes: '',
  SupportRenewalDeclined: false,
  CustomerIsDealer: false,
  CustomerIsTrial: false,
};

const defaultEmployee = {
  CustomerID: undefined,
  EmployeeName: '',
  EmployeePhone1: '',
  EmployeePhone2: '',
  EmployeeFax: '',
  EmployeeEmail: '',
  EmployeeNotes: '',
  EmployeeTitleID: undefined,
  EmployeeTitle: undefined,
};

const defaultComputer = {
  CustomerID: undefined,
  SerialNumber: '',
  ProductKey: '',
  RegistrationKey: undefined,
  SONumber: '',
  ComputerNotes: '',
  SoftwareTypeID: undefined,
  ComputerDongle: '',
  PurchasingVendorID: undefined,
};

const defaultUser = {
  UserGivenName: '',
  UserLocation: '',
  UserPhoneNumber: '',
  UserEmailAddress: '',
  UserNotes: '',
};

const defaultActiveUserUpdatePassword = {
  CurrentPassword: process.env.VUE_APP_PASSWORD,
  NewPassword: undefined,
  NewPasswordConfirm: undefined,
};

const defaultSupportContract = {
  SupportContractID: undefined,
  SupportContractStartUtc: undefined,
  SupportContractEndUtc: undefined,
  SupportContractNotes: '',
};

const defaultUserPermissions = [];
/* / Default Entities */

/* Default Search Parameters */
const dateFormat = 'YYYY-MM-DD[T]HH:mm:ssZ';
const todayFormat = 'YYYY-MM-DD';
const now = moment().endOf('day');

const defaultCallLogSearchParameters = {
  CustomerID: undefined,
  ProductKey: undefined,
  SerialNumber: undefined,
  PhoneNumber: undefined,
  EmployeeName: undefined,
  HiddenStatus: 'ShowNotHidden',
  StartDateUtc: now.clone().subtract(3, 'months').format(dateFormat),
  EndDateUtc: now.clone().format(dateFormat),
  TimeLastRefreshed: now.clone().format(todayFormat),
  ViewSummary: true,
  CallProblem: undefined,
  CallNotes: undefined,
};

const defaultReportSearchParameters = {
  CustomerID: undefined,
  ProductKey: undefined,
  SerialNumber: undefined,
  PhoneNumber: undefined,
  EmployeeName: undefined,
  HiddenStatus: 'ShowNotHidden',
  StartDateUtc: now.clone().subtract(3, 'months').format(dateFormat),
  EndDateUtc: now.clone().format(dateFormat),
  TimeLastRefreshed: now.clone().format(todayFormat),
  ViewSummary: true,
  CallProblem: undefined,
  CallNotes: undefined,
};

function getDefaultCallLogSearchParameters() {
  const callLogSearchParameters = browserStorage.localStorageGet(
    'callLogSearchParameters',
    defaultCallLogSearchParameters,
  );

  const now = moment().endOf('day');
  const dateFormat = 'YYYY-MM-DD[T]HH:mm:ssZ';
  const todayFormat = 'YYYY-MM-DD';
  const todaysDate = now.clone().format(todayFormat);

  if (callLogSearchParameters.TimeLastRefreshed !== todaysDate) {
    callLogSearchParameters.StartDateUtc = now.clone().subtract(3, 'months').format(dateFormat);
    callLogSearchParameters.EndDateUtc = now.clone().format(dateFormat);
    callLogSearchParameters.TimeLastRefreshed = now.clone().format(todayFormat);
    callLogSearchParameters.ViewSummary = true;
  }

  return callLogSearchParameters;
}

function getDefaultReportSearchParameters() {
  const reportSearchParameters = browserStorage.localStorageGet(
    'reportSearchParameters',
    defaultReportSearchParameters,
  );

  const now = moment().endOf('day');
  const dateFormat = 'YYYY-MM-DD[T]HH:mm:ssZ';
  const todayFormat = 'YYYY-MM-DD';
  const todaysDate = now.clone().format(todayFormat);

  if (reportSearchParameters.TimeLastRefreshed !== todaysDate) {
    reportSearchParameters.StartDateUtc = now.clone().subtract(3, 'months').format(dateFormat);
    reportSearchParameters.EndDateUtc = now.clone().format(dateFormat);
    reportSearchParameters.TimeLastRefreshed = now.clone().format(todayFormat);
    reportSearchParameters.ViewSummary = true;
  }

  return reportSearchParameters;
}

const defaultCustomerSearchParameters = {
  CustomerTypeID: undefined,
  ContractEndBeforeUtc: undefined,
  SupportRenewalDeclined: undefined,
  CustomerStatusColorName: undefined,
  ProductKey: undefined,
  EmployeeName: undefined,
  CourtesyCall: false,
  HiddenStatus: 'ShowNotHidden',
};

const defaultUserSearchParameters = {
  HiddenStatus: 'ShowNotHidden',
};

const defaultTransactionHistorySearchParameters = {
  Entity: 'Call',
  UserID: undefined,
  StartDateUtc: now.clone().subtract(3, 'months').format(dateFormat),
  EndDateUtc: now.clone().format(dateFormat),
};
/* / Default Search Parameters */

const store = new Vuex.Store({
  state: {
    loadingMessage: '',
    token: token || '',
    referrer: null,
    user: {},
    userUpdatePasswordModalFormOpen: false,
    activeUserUpdatePassword: _.cloneDeep(defaultActiveUserUpdatePassword),
    activeUserUpdatePasswordCopy: _.cloneDeep(defaultActiveUserUpdatePassword),
    userPermissions: [],
    callLog: [],
    openCalls: [],
    activeCall: _.cloneDeep(defaultCall),
    activeCallCopy: _.cloneDeep(defaultCall),
    customers: [],
    allCustomers: [],
    activeCustomer: _.cloneDeep(defaultCustomer),
    activeCustomerCopy: _.cloneDeep(defaultCustomer),
    customerModalFormOpen: false,
    callStatuses: [],
    callTypes: [],
    customerTypes: [],
    employees: [],
    customerEmployees: [],
    customerEmployeesForSearch: [],
    customerEmployeesForDropdown: [],
    activeEmployee: _.cloneDeep(defaultEmployee),
    activeEmployeeCopy: _.cloneDeep(defaultEmployee),
    employeeModalFormOpen: false,
    employeeTitles: [],
    computers: [],
    customerComputers: [],
    customerComputersForDropdown: [],
    activeComputer: _.cloneDeep(defaultComputer),
    activeComputerCopy: _.cloneDeep(defaultComputer),
    computerModalFormOpen: false,
    users: [],
    allUsers: [],
    activeUser: {},
    activeUserCopy: {},
    activeUserPermissions: [],
    activeUserPermissionsCopy: [],
    availablePermissions: [],
    roles: [],
    softwareTypes: [],
    purchasingVendors: [],
    supportContracts: [],
    activeSupportContract: _.cloneDeep(defaultSupportContract),
    activeSupportContractCopy: _.cloneDeep(defaultSupportContract),
    supportContractModalFormOpen: false,
    codeModalFormOpen: false,
    activeCode: {},
    activeCodeCopy: {},
    activeCodeConfig: {},
    transactionHistory: [],
    transactionHistoryEntities: [],
    activeRequests: [],
    supportRenewalStatuses: [
      {
        SupportRenewalDeclined: false,
        SupportRenewalStatus: 'Accepted',
      },
      {
        SupportRenewalDeclined: true,
        SupportRenewalStatus: 'Declined',
      },
    ],
    hiddenStatuses: [
      {
        value: 'ShowHidden',
        text: 'Show Hidden',
      },
      {
        value: 'ShowNotHidden',
        text: 'Show Not Hidden',
      },
      {
        value: 'ShowAny',
        text: 'Show Any',
      },
    ],
    states,
    customerStatuses: [],
    computerSupportContracts: [],

    /* Search Parameters */
    callLogSearchParameters: getDefaultCallLogSearchParameters(),
    reportSearchParameters: getDefaultReportSearchParameters(),
    customerSearchParameters: browserStorage.localStorageGet(
      'customerSearchParameters',
      defaultCustomerSearchParameters,
    ),
    userSearchParameters: browserStorage.localStorageGet(
      'userSearchParameters',
      defaultUserSearchParameters,
    ),
    transactionHistorySearchParameters: browserStorage.localStorageGet(
      'transactionHistorySearchParameters',
      defaultTransactionHistorySearchParameters,
    ),
    /* / Search Parameters */
  },
  getters: {
    isLoggedIn: (state) => !!state.token,
    isLoadingActive: (state) => state.activeRequests.length > 0,
    isActiveCallDirty: (state) => {
      const propsToCheck = [
        'CustomerID',
        'EmployeeID',
        'ComputerID',
        'CallTypeID',
        'CallProblem',
        'CallNotes',
        'TechName',
        'CallStatusID',
      ];

      const call = _.pick(state.activeCall, propsToCheck);
      const callCopy = _.pick(state.activeCallCopy, propsToCheck);

      return !_.isEqual(call, callCopy);
    },
    isActiveCustomerDirty: (state) => {
      const propsToCheck = [
        'CustomerName',
        'CustomerTypeID',
        'CustomerWebsite',
        'SupportRenewalDeclined',
        'CustomerAddress1',
        'CustomerAddress1',
        'CustomerCity',
        'CustomerState',
        'CustomerZipCode',
        'CustomerPhone1',
        'CustomerPhone2',
        'CustomerFax',
        'CustomerEmail',
        'SupervisorEmployeeID',
        'BillingAddress1',
        'BillingAddress2',
        'BillingCity',
        'BillingState',
        'BillingZipCode',
        'BillingContactEmployeeID',
        'CustomerNotes',
        'CustomerIsDealer',
        'CustomerIsTrial',
      ];

      const customer = _.pick(state.activeCustomer, propsToCheck);
      const customerCopy = _.pick(state.activeCustomerCopy, propsToCheck);

      return !_.isEqual(customer, customerCopy);
    },
    isActiveEmployeeDirty: (state) => {
      const propsToCheck = [
        'EmployeeName',
        'EmployeePhone1',
        'EmployeePhone2',
        'EmployeeFax',
        'EmployeeEmail',
        'EmployeeNotes',
        'EmployeeTitleID',
      ];

      const employee = _.pick(state.activeEmployee, propsToCheck);
      const employeeCopy = _.pick(state.activeEmployeeCopy, propsToCheck);

      return !_.isEqual(employee, employeeCopy);
    },
    isActiveComputerDirty: (state) => {
      const propsToCheck = [
        'SerialNumber',
        'ProductKey',
        'RegistrationKey',
        'SONumber',
        'ComputerNotes',
        'SoftwareTypeID',
        'ComputerDongle',
        'PurchasingVendorID',
      ];

      const computer = _.pick(state.activeComputer, propsToCheck);
      const computerCopy = _.pick(state.activeComputerCopy, propsToCheck);

      return !_.isEqual(computer, computerCopy);
    },
    isActiveSupportContractDirty: (state) => {
      const propsToCheck = [
        'SupportContractStartUtc',
        'SupportContractEndUtc',
        'SupportContractNotes',
      ];

      const supportContract = _.pick(state.activeSupportContract, propsToCheck);
      const supportContractCopy = _.pick(state.activeSupportContractCopy, propsToCheck);

      return !_.isEqual(supportContract, supportContractCopy);
    },
    isActiveUserDirty: (state) => {
      const propsToCheck = [
        'UserGivenName',
        'UserLocation',
        'UserPhoneNumber',
        'UserEmailAddress',
        'UserNotes',
      ];

      const user = _.pick(state.activeUser, propsToCheck);
      const userCopy = _.pick(state.activeUserCopy, propsToCheck);

      const userPermissionCodes = _(state.activeUserPermissions)
        .map('PermissionCode')
        .orderBy()
        .value();

      const userPermissionCodesCopy = _(state.activeUserPermissionsCopy)
        .map('PermissionCode')
        .orderBy()
        .value();

      return !_.isEqual(user, userCopy) || !_.isEqual(userPermissionCodes, userPermissionCodesCopy);
    },
    isActiveCodeDirty: (state) => !_.isEqual(state.activeCode, state.activeCodeCopy),
    isUserUpdatePasswordFormDirty: (state) => !_.isEqual(
      state.activeUserUpdatePassword,
      state.activeUserUpdatePasswordCopy,
    ),
    productKeys(state) {
      return _(state.computers)
        .map('ProductKey')
        .compact()
        .uniq()
        .map((productKey) => ({
          ProductKeyName: productKey,
          ProductKey: productKey,
        }))
        .value();
    },
    serialNumbers(state) {
      return _(state.computers)
        .map('SerialNumber')
        .compact()
        .uniq()
        .map((serialNumber) => ({
          SerialNumberName: serialNumber,
          SerialNumber: serialNumber,
        }))
        .value();
    },
    employeePhoneNumbers(state) {
      const firstPhoneNumbers = _.map(
        state.employees,
        'EmployeePhone1',
      );

      const secondPhoneNumbers = _.map(
        state.employees,
        'EmployeePhone2',
      );

      return _(firstPhoneNumbers)
        .concat(secondPhoneNumbers)
        .compact()
        .uniq()
        .map((phoneNumber) => ({
          PhoneNumberName: phoneNumber,
          PhoneNumber: phoneNumber,
        }))
        .value();
    },
  },
  mutations: {
    /* Loading / Busy */
    setActiveRequests(state, activeRequests) {
      state.activeRequests = activeRequests;
    },
    addActiveRequest(state, requestData) {
      state.activeRequests.push(requestData);
      state.loadingMessage = 'Loading data...';
    },
    /* / Loading / Busy */

    /* Auth */
    authSuccess(state, user) {
      state.token = user.AuthToken;
      state.user = user;
    },
    authError(state, route) {
      const referrer = {
        name: route.name,
        params: _.clone(route.params),
      };

      state.referrer = referrer;
    },
    clearAuthError(state) {
      state.referrer = null;
    },
    clearToken(state) {
      state.token = '';
      browserStorage.localStorageDelete('token');
      delete axios.defaults.headers.common.Authorization;
    },
    setUserUpdatePasswordModalFormOpen(state, userUpdatePasswordModalFormOpen) {
      state.userUpdatePasswordModalFormOpen = userUpdatePasswordModalFormOpen;
    },
    setActiveUserUpdatePassword(state, activeUserUpdatePassword) {
      if (!activeUserUpdatePassword) {
        activeUserUpdatePassword = _.clone(defaultActiveUserUpdatePassword);
      }

      state.activeUserUpdatePassword = activeUserUpdatePassword;
      state.activeUserUpdatePasswordCopy = _.cloneDeep(state.activeUserUpdatePasswordCopy);
    },
    signOut(state) {
      state.loadingMessage = '';
      state.token = '';
      state.user = {};
      state.userPermissions = [];
      state.callLog = [];
      state.openCalls = [];
      state.activeCall = [];
      state.activeCallCopy = [];
      state.customers = [];
      state.allCustomers = [];
      state.activeCustomer = {};
      state.activeCustomerCopy = {};
      state.customerTypes = [];
      state.callStatuses = [];
      state.callTypes = [];
      state.employees = [];
      state.customerEmployees = [];
      state.customerEmployeesForDropdown = [];
      state.activeEmployee = {};
      state.activeEmployeeCopy = {};
      state.employeeTitles = [];
      state.computers = [];
      state.customerComputers = [];
      state.customerComputersForDropdown = [];
      state.activeComputer = {};
      state.activeComputerCopy = {};
      state.users = [];
      state.allUsers = [];
      state.activeUser = {};
      state.activeUserCopy = {};
      state.activeUserPermissions = [];
      state.activeUserPermissionsCopy = [];
      state.availablePermissions = [];
      state.roles = [];
      state.softwareTypes = [];
      state.purchasingVendors = [];
      state.supportContracts = [];
      state.activeSupportContract = {};
      state.activeSupportContractCopy = {};
      state.activeCode = {};
      state.activeCodeCopy = {};
      state.activeCodeConfig = {};
      state.transactionHistory = [];
      state.transactionHistoryEntities = [];
      state.customerStatuses = [];
      state.computerSupportContracts = [];

      /* Search Parameters */
      state.callLogSearchParameters = _.clone(defaultCallLogSearchParameters);
      state.reportSearchParameters = _.clone(defaultReportSearchParameters);
      state.customerSearchParameters = _.clone(defaultCustomerSearchParameters);
      state.userSearchParameters = _.clone(defaultUserSearchParameters);
      state.transactionHistorySearchParameters = _.clone(defaultTransactionHistorySearchParameters);
      /* / Search Parameters */

      delete axios.defaults.headers.common.Authorization;
      browserStorage.localStorageClear();
    },
    /* / Auth */

    /* Call Log */
    setCallLog(state, callLog) {
      state.callLog = callLog;
    },
    setActiveCall(state, activeCall) {
      if (!activeCall) {
        activeCall = _.clone(defaultCall);
      }

      state.activeCall = activeCall;
      state.activeCallCopy = _.cloneDeep(state.activeCall);
    },
    setOpenCalls(state, openCalls) {
      state.openCalls = openCalls;
    },
    setCallStatuses(state, callStatuses) {
      state.callStatuses = callStatuses;
    },
    setCallTypes(state, callTypes) {
      state.callTypes = callTypes;
    },
    resetDateRangeFilter(state, dateRange) {
      state.callLogSearchParameters.StartDateUtc = dateRange.StartDateUtc;
      state.callLogSearchParameters.EndDateUtc = dateRange.EndDateUtc;
    },
    setReportFiltersToEmpty(state) {
      state.callLogSearchParameters.CallProblem = undefined;
      state.callLogSearchParameters.CallNotes = undefined;
    },
    /* / Call Log */

    /* Customers */
    setCustomers(state, customers) {
      state.customers = customers;
    },
    setActiveCustomer(state, activeCustomer) {
      if (!activeCustomer) {
        activeCustomer = _.clone(defaultCustomer);
      }

      state.activeCustomer = activeCustomer;
      state.activeCustomerCopy = _.cloneDeep(state.activeCustomer);
    },
    setCustomerModalFormOpen(state, customerModalFormOpen) {
      state.customerModalFormOpen = customerModalFormOpen;
    },
    setAllCustomers(state, allCustomers) {
      state.allCustomers = allCustomers;
    },
    setCustomerTypes(state, customerTypes) {
      state.customerTypes = customerTypes;
    },
    setCustomerStatuses(state, customerStatuses) {
      state.customerStatuses = customerStatuses;
    },
    /* / Customers */

    /* Employees */
    setEmployees(state, employees) {
      state.employees = employees;
    },
    setCustomerEmployeesForSearch(state, customerEmployeesForSearch) {
      state.customerEmployeesForSearch = customerEmployeesForSearch;
    },
    setCustomerEmployees(state, customerEmployees) {
      state.customerEmployees = customerEmployees;
    },
    setCustomerEmployeesForDropdown(state, customerEmployeesForDropdown) {
      state.customerEmployeesForDropdown = customerEmployeesForDropdown;
    },
    setEmployeeTitles(state, employeeTitles) {
      state.employeeTitles = employeeTitles;
    },
    setActiveEmployee(state, activeEmployee) {
      if (!activeEmployee) {
        activeEmployee = _.clone(defaultEmployee);
      }

      state.activeEmployee = activeEmployee;
      state.activeEmployeeCopy = _.cloneDeep(state.activeEmployee);
    },
    setEmployeeModalFormOpen(state, employeeModalFormOpen) {
      state.employeeModalFormOpen = employeeModalFormOpen;
    },
    /* / Employees */

    /* Computers */
    setComputers(state, computers) {
      state.computers = computers;
    },
    setActiveComputer(state, activeComputer) {
      if (!activeComputer) {
        activeComputer = _.clone(defaultComputer);
      }

      state.activeComputer = activeComputer;
      state.activeComputerCopy = _.cloneDeep(state.activeComputer);
    },
    setComputerModalFormOpen(state, computerModalFormOpen) {
      state.computerModalFormOpen = computerModalFormOpen;
    },
    setSoftwareTypes(state, softwareTypes) {
      state.softwareTypes = softwareTypes;
    },
    setPurchasingVendors(state, purchasingVendors) {
      state.purchasingVendors = purchasingVendors;
    },
    setCustomerComputers(state, customerComputers) {
      state.customerComputers = customerComputers;
    },
    setCustomerComputersForDropdown(state, customerComputersForDropdown) {
      state.customerComputersForDropdown = customerComputersForDropdown;
    },
    /* / Computers */

    /* Users */
    setUsers(state, users) {
      state.users = users;
    },
    setAllUsers(state, allUsers) {
      state.allUsers = allUsers;
    },
    setRoles(state, roles) {
      state.roles = roles;
    },
    setAvailablePermissions(state, availablePermissions) {
      state.availablePermissions = availablePermissions;
    },
    setActiveUser(state, activeUser) {
      if (!activeUser) {
        activeUser = _.clone(defaultUser);
      }

      state.activeUser = activeUser;
      state.activeUserCopy = _.cloneDeep(state.activeUser);
    },
    setActiveUserPermissions(state, activeUserPermissions) {
      if (!activeUserPermissions) {
        activeUserPermissions = _.clone(defaultUserPermissions);
      }

      state.activeUserPermissions = activeUserPermissions;
      state.activeUserPermissionsCopy = _.cloneDeep(state.activeUserPermissions);
    },
    setUserPermissions(state, userPermissions) {
      state.userPermissions = userPermissions;
    },
    /* / Users */

    /* Support Contracts */
    setSupportContracts(state, supportContracts) {
      state.supportContracts = supportContracts;
    },
    setActiveSupportContract(state, activeSupportContract) {
      if (!activeSupportContract) {
        activeSupportContract = _.clone(defaultSupportContract);
      }

      state.activeSupportContract = activeSupportContract;
      state.activeSupportContractCopy = _.cloneDeep(state.activeSupportContract);
    },
    setSupportContractModalFormOpen(state, supportContractModalFormOpen) {
      state.supportContractModalFormOpen = supportContractModalFormOpen;
    },
    setComputerSupportContracts(state, computerSupportContracts) {
      state.computerSupportContracts = computerSupportContracts;
    },
    /* / Support Contracts */

    /* Code */
    closeCodeModalForm(state) {
      state.codeModalFormOpen = false;
      setTimeout(() => {
        state.activeCode = {};
        state.activeCodeCopy = {};
        state.activeCodeConfig = {};
      });
    },
    /* / Code */

    /* Transaction History */
    setTransactionHistory(state, transactionHistory) {
      state.transactionHistory = transactionHistory;
    },
    setTransactionHistoryEntities(state, transactionHistoryEntities) {
      state.transactionHistoryEntities = transactionHistoryEntities;
    },
    /* / Transaction History */

    /* Search Parameters */
    setSearchParameters(state, payload = {}) {
      _.each(payload.params, (value, key) => {
        Vue.set(state[payload.paramsKey], key, value);
      });

      browserStorage.localStorageSet(
        payload.paramsKey,
        state[payload.paramsKey],
      );
    },
    /* / Search Parameters */
  },
  actions: {
    /* Init App */
    initialize({ commit, state }, payload = {}) {
      let initPromise;

      if (state.token) {
        const activeRequestID = 'initialize';
        let requestPromise = Promise.resolve();

        if (payload.tokenSignIn) {
          requestPromise = axios.post('Users/SignInWithAuthToken').then(returnData)
            .then((user) => {
              commit('authSuccess', user);
              return axios.get(
                'Users/GetAssignedPermissionsByUserID',
                { params: { UserID: user.UserID } },
              );
            })
            .then(({ data }) => {
              commit('setUserPermissions', data);
            });

          commit('addActiveRequest', {
            activeRequestID,
            requestPromise,
          });
        }

        initPromise = requestPromise;
      } else {
        initPromise = Promise.reject(new Error('User is not signed in.'));
      }

      return initPromise;
    },
    /* / Init App */

    /* Call Log */
    getCall({ commit }, payload = {}) {
      const activeRequestID = 'getCall';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Calls/GetByID',
          { params: payload.params },
        ).then(({ data }) => {
          commit('setActiveCall', data);
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCalls({ state, commit }, payload = {}) {
      const activeRequestID = 'getCalls';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Calls/GetAll',
          { params: state.callLogSearchParameters },
        ).then(({ data }) => commit('setCallLog', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getReportCalls({ state, commit }, payload = {}) {
      const activeRequestID = 'getCalls';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (payload.params !== undefined) {
        payload.params.ViewSummary = true;
      } else {
        payload.params = { ViewSummary: true };
      }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Calls/GetAll',
          { params: state.reportSearchParameters },
        ).then(({ data }) => commit('setCallLog', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getOpenCalls({ commit }, payload = {}) {
      const activeRequestID = 'getOpenCalls';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Calls/GetMyOpen',
        ).then(({ data }) => commit('setOpenCalls', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCallStatuses({ commit }, payload = {}) {
      const activeRequestID = 'getCallStatuses';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Calls/GetAllCallStatus',
        ).then(({ data }) => commit('setCallStatuses', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCallTypes({ commit }, payload = {}) {
      const activeRequestID = 'getCallTypes';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Calls/GetAllCallTypes',
        ).then(({ data }) => commit('setCallTypes', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    createCall({ commit, dispatch }, payload = {}) {
      const activeRequestID = 'createCall';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      const data = _.pick(
        payload.call,
        ['CustomerID', 'EmployeeID', 'ComputerID'],
      );

      data.CallNotes = '';

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Calls/Create',
          data,
        ).then(({ data }) => {
          commit('setActiveCall', data);
          return dispatch('getOpenCalls')
            .then(() => data);
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    updateCall({ commit, dispatch }, payload = {}) {
      const activeRequestID = 'updateCall';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Calls/Update',
          payload.call,
        ).then(({ data }) => {
          commit('setActiveCall', data);
          return Promise.all([
            dispatch('getCalls'),
            dispatch('getOpenCalls'),
          ]).then(() => data);
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    endCall({ commit, dispatch }, payload = {}) {
      const activeRequestID = 'endCall';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Calls/EndCall',
          payload.call,
        ).then(({ data }) => Promise.all([
          dispatch('getCalls'),
          dispatch('getOpenCalls'),
        ]).then(() => commit('setActiveCall', data)));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    deleteCall({ commit, dispatch }, payload = {}) {
      const activeRequestID = 'deleteCall';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Calls/Delete',
          payload.call,
        ).then(() => {
          commit('setActiveCall');
          return Promise.all([
            dispatch('getCalls'),
            dispatch('getOpenCalls'),
          ]);
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    restoreCall({ commit, dispatch }, payload = {}) {
      const activeRequestID = 'restoreCall';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Calls/Restore',
          payload.call,
        ).then(() => {
          commit('setActiveCall');
          return Promise.all([
            dispatch('getCalls'),
            dispatch('getOpenCalls'),
          ]);
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    resetDateRangeFilter({ commit }) {
      const today = moment().endOf('day');
      const dateRange = {};

      dateRange.StartDateUtc = today.clone().subtract(3, 'months').format(dateFormat);
      dateRange.EndDateUtc = today.clone().format(dateFormat);
      commit('resetDateRangeFilter', dateRange);
    },
    /* / Call Log */

    /* Customers */
    getCustomer({ commit }, payload = {}) {
      const activeRequestID = 'getCustomer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Customers/GetByID',
          { params: payload.params },
        ).then(({ data }) => commit('setActiveCustomer', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCustomers({ state, commit }, payload = {}) {
      const activeRequestID = 'getCustomers';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (payload.params !== undefined) {
        payload.params.ViewSummary = true;
      } else {
        payload.params = { ViewSummary: true };
      }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Customers/GetAll',
          { params: state.customerSearchParameters },
        ).then(({ data }) => commit('setCustomers', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getAllCustomers({ commit }, payload = {}) {
      const activeRequestID = 'getAllCustomers';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (payload.params !== undefined) {
        payload.params.ViewSummary = true;
      } else {
        payload.params = { ViewSummary: true };
      }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Customers/GetAll',
          { params: payload.params },
        ).then(({ data }) => commit('setAllCustomers', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCustomerTypes({ commit }, payload = {}) {
      const activeRequestID = 'getCustomerTypes';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Customers/GetAllCustomerTypes',
        ).then(({ data }) => commit('setCustomerTypes', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    createCustomer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'createCustomer';
      const requestAlreadyActive = isRequestActive(activeRequestID);

      const data = _.pick(
        state.activeCustomer,
        [
          'CustomerName',
          'CustomerTypeID',
          'SupervisorEmployeeID',
          'CustomerPhone1',
          'CustomerPhone2',
          'CustomerFax',
          'CustomerEmail',
          'CustomerWebsite',
          'CustomerAddress1',
          'CustomerAddress2',
          'CustomerCity',
          'CustomerState',
          'CustomerZipCode',
          'BillingAddress1',
          'BillingAddress2',
          'BillingCity',
          'BillingState',
          'BillingZipCode',
          'BillingContactEmployeeID',
          'SupportRenewalDeclined',
          'CustomerNotes',
          'CustomerIsDealer',
          'CustomerIsTrial',
        ],
      );

      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Customers/Create',
          data,
        ).then(({ data }) => {
          if (payload.resetToCustomer) {
            commit('setActiveCustomer', data);
          }

          return dispatch('getCustomers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }
    },
    updateCustomer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'updateCustomer';
      const requestAlreadyActive = isRequestActive(activeRequestID);

      const data = _.pick(
        state.activeCustomer,
        [
          'CustomerID',
          'CustomerName',
          'CustomerTypeID',
          'SupervisorEmployeeID',
          'CustomerPhone1',
          'CustomerPhone2',
          'CustomerFax',
          'CustomerEmail',
          'CustomerWebsite',
          'CustomerAddress1',
          'CustomerAddress2',
          'CustomerCity',
          'CustomerState',
          'CustomerZipCode',
          'BillingAddress1',
          'BillingAddress2',
          'BillingCity',
          'BillingState',
          'BillingZipCode',
          'BillingContactEmployeeID',
          'SupportRenewalDeclined',
          'CustomerNotes',
          'CustomerRowVersion',
          'CustomerIsDealer',
          'CustomerIsTrial',
        ],
      );

      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Customers/Update',
          data,
        ).then(({ data }) => {
          if (payload.resetToCustomer) {
            commit('setActiveCustomer', data);
          }

          return dispatch('getCustomers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }
    },
    deleteCustomer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'deleteCustomer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Customers/Delete',
          state.activeCustomer,
        ).then(() => dispatch('getCustomers'));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }
    },
    restoreCustomer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'restoreCustomer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Customers/Restore',
          state.activeCustomer,
        ).then(() => dispatch('getCustomers'));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }
    },
    openCustomerModalForm({ commit, dispatch }, payload = {}) {
      const newCustomer = _.merge(
        {},
        defaultCustomer,
        payload.customer,
      );

      let customerPromise = Promise.resolve(newCustomer);

      if (payload.customer && payload.customer.CustomerID) {
        customerPromise = dispatch('getCustomer', {
          params: { CustomerID: payload.customer.CustomerID },
        });
      } else {
        customerPromise
          .then((newCustomer) => commit('setActiveCustomer', newCustomer));
      }

      return customerPromise
        .then(() => commit('setCustomerModalFormOpen', true));
    },
    getCustomerStatuses({ commit }, payload = {}) {
      const activeRequestID = 'getCustomerStatuses';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Customers/GetAllCustomerStatuses',
        ).then(({ data }) => commit('setCustomerStatuses', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    /* / Customers */

    /* Employees */
    getEmployee({ commit }, payload = {}) {
      const activeRequestID = 'getEmployee';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Employees/GetByID',
          { params: payload.params },
        ).then(({ data }) => commit('setActiveEmployee', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getEmployees({ commit }, payload = {}) {
      const activeRequestID = 'getEmployees';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (payload.params !== undefined) {
        payload.params.ViewSummary = true;
      } else {
        payload.params = { ViewSummary: true };
      }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Employees/GetAll',
          { params: { ViewSummary: true } },
        ).then(({ data }) => commit('setEmployees', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCustomerEmployees({ commit }, payload = {}) {
      const activeRequestID = 'getCustomerEmployees';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (payload.params !== undefined) {
        payload.params.ViewSummary = true;
      } else {
        payload.params = { ViewSummary: true };
      }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Employees/GetAll',
          { params: payload.params },
        ).then(({ data }) => {
          commit('setCustomerEmployees', data);
          return data;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCustomerEmployeesForDropdown({ commit }, payload = {}) {
      const activeRequestID = 'getCustomerEmployeesForDropdown';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (payload.params !== undefined) {
        payload.params.ViewSummary = true;
      } else {
        payload.params = { ViewSummary: true };
      }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Employees/GetAll',
          { params: payload.params },
        ).then(({ data }) => commit('setCustomerEmployeesForDropdown', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getEmployeeTitles({ commit }, payload = {}) {
      const activeRequestID = 'getEmployeeTitles';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Employees/GetAllEmployeeTitles',
        ).then(({ data }) => commit('setEmployeeTitles', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    createEmployee({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'createEmployee';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Employees/Create',
          state.activeEmployee,
        ).then(() => {
          EventBus.$emit('getCustomerEmployees');

          const employeesForDropdownPromise = dispatch('getCustomerEmployeesForDropdown', {
            params: {
              CustomerID: state.activeEmployee.CustomerID,
            },
          });

          return employeesForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    updateEmployee({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'updateEmployee';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Employees/Update',
          state.activeEmployee,
        ).then(() => {
          EventBus.$emit('getCustomerEmployees');

          const employeesForDropdownPromise = dispatch('getCustomerEmployeesForDropdown', {
            params: {
              CustomerID: state.activeEmployee.CustomerID,
            },
          });

          return employeesForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    deleteEmployee({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'deleteEmployee';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Employees/Delete',
          state.activeEmployee,
        ).then(() => {
          EventBus.$emit('getCustomerEmployees');

          const employeesForDropdownPromise = dispatch('getCustomerEmployeesForDropdown', {
            params: {
              CustomerID: state.activeEmployee.CustomerID,
            },
          });

          return employeesForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    restoreEmployee({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'restoreEmployee';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Employees/Restore',
          state.activeEmployee,
        ).then(() => {
          EventBus.$emit('getCustomerEmployees');

          const employeesForDropdownPromise = dispatch('getCustomerEmployeesForDropdown', {
            params: {
              CustomerID: state.activeEmployee.CustomerID,
            },
          });

          return employeesForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    openEmployeeModalForm({ commit, dispatch }, payload = {}) {
      const newEmployee = _.merge(
        {},
        defaultEmployee,
        payload.employee,
      );

      let employeePromise = Promise.resolve(newEmployee);

      if (payload.employee && payload.employee.EmployeeID) {
        employeePromise = dispatch('getEmployee', {
          params: { EmployeeID: payload.employee.EmployeeID },
        });
      } else {
        employeePromise
          .then((newEmployee) => commit('setActiveEmployee', newEmployee));
      }

      return employeePromise
        .then(() => commit('setEmployeeModalFormOpen', true));
    },
    /* / Employees */

    /* Computers */
    getComputer({ commit }, payload = {}) {
      const activeRequestID = 'getComputer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Computers/GetByID',
          { params: payload.params },
        ).then(({ data }) => commit('setActiveComputer', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getComputers({ commit }, payload = {}) {
      const activeRequestID = 'getComputers';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      // if (payload.params !== undefined) {
      //   payload.params.ViewSummary = true;
      // } else {
      //   payload.params = { ViewSummary: true };
      // }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Computers/GetAll',
          { params: payload.params },
        ).then(({ data }) => commit('setComputers', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCustomerComputers({ commit }, payload = {}) {
      const activeRequestID = 'getCustomerComputers';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      // if (payload.params !== undefined) {
      //   payload.params.ViewSummary = true;
      // } else {
      //   payload.params = { ViewSummary: true };
      // }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Computers/GetAll',
          { params: payload.params },
        ).then(({ data }) => {
          commit('setCustomerComputers', data);
          return data;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getCustomerComputersForDropdown({ commit }, payload = {}) {
      const activeRequestID = 'getCustomerComputersForDropdown';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      // if (payload.params !== undefined) {
      //   payload.params.ViewSummary = true;
      // } else {
      //   payload.params = { ViewSummary: true };
      // }

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Computers/GetAll',
          { params: payload.params },
        ).then(({ data }) => commit('setCustomerComputersForDropdown', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getSoftwareTypes({ commit }, payload = {}) {
      const activeRequestID = 'getSoftwareTypes';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Computers/GetAllSoftwareTypes',
          { params: payload.params },
        ).then(({ data }) => commit('setSoftwareTypes', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    hideSoftwareType({ commit, dispatch }, payload = {}) {
      const activeRequestID = 'getSoftwareTypes';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Computers/DeleteSoftwareType',
          { SoftwareTypeID: payload.SoftwareTypeID },
        ).then(() => {
          const softwareTypeParams = { params: { HiddenStatus: 'ShowAny' }, override: true };
          dispatch('getSoftwareTypes', softwareTypeParams);
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    unhideSoftwareType({ commit, dispatch }, payload = {}) {
      const activeRequestID = 'getSoftwareTypes';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Computers/RestoreSoftwareType',
          { SoftwareTypeID: payload.SoftwareTypeID },
        ).then(() => {
          const softwareTypeParams = { params: { HiddenStatus: 'ShowAny' }, override: true };
          dispatch('getSoftwareTypes', softwareTypeParams);
        });
        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getPurchasingVendors({ commit }, payload = {}) {
      const activeRequestID = 'getPurchasingVendors';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Computers/GetAllPurchasingVendors',
        ).then(({ data }) => commit('setPurchasingVendors', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    createComputer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'createComputer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Computers/Create',
          state.activeComputer,
        ).then(() => {
          EventBus.$emit('getCustomerComputers');

          const computersForDropdownPromise = dispatch('getCustomerComputersForDropdown', {
            params: {
              CustomerID: state.activeComputer.CustomerID,
            },
          });

          return computersForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    updateComputer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'updateComputer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Computers/Update',
          state.activeComputer,
        ).then(() => {
          EventBus.$emit('getCustomerComputers');

          const computersForDropdownPromise = dispatch('getCustomerComputersForDropdown', {
            params: {
              CustomerID: state.activeComputer.CustomerID,
            },
          });

          return computersForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    deleteComputer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'deleteComputer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Computers/Delete',
          state.activeComputer,
        ).then(() => {
          EventBus.$emit('getCustomerComputers');

          const computersForDropdownPromise = dispatch('getCustomerComputersForDropdown', {
            params: {
              CustomerID: state.activeComputer.CustomerID,
            },
          });

          return computersForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    restoreComputer({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'restoreComputer';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Computers/Restore',
          state.activeComputer,
        ).then(() => {
          EventBus.$emit('getCustomerComputers');

          const computersForDropdownPromise = dispatch('getCustomerComputersForDropdown', {
            params: {
              CustomerID: state.activeComputer.CustomerID,
            },
          });

          return computersForDropdownPromise;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    openComputerModalForm({ commit, dispatch }, payload = {}) {
      const newComputer = _.merge(
        {},
        defaultComputer,
        payload.computer,
      );

      let computerPromise = Promise.resolve(newComputer);

      if (payload.computer && payload.computer.ComputerID) {
        computerPromise = dispatch('getComputer', {
          params: { ComputerID: payload.computer.ComputerID },
        });
      } else {
        computerPromise
          .then((newComputer) => commit('setActiveComputer', newComputer));
      }

      return computerPromise
        .then(() => commit('setComputerModalFormOpen', true));
    },
    generateKey({ commit, state }, payload = {}) {
      const activeRequestID = 'generateKey';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      const data = _.pick(
        state.activeComputer,
        [
          'ProductKey',
          'SoftwareTypeID',
          'SerialNumber',
          'ComputerDongle',
        ],
      );

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'RegistrationKeys/Generate',
          data,
        ).then(({ data }) => {
          state.activeComputer.RegistrationKey = data.RegistrationKey;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    transferComputer({ commit }, payload = {}) {
      /* eslint-disable */
      const activeRequestID = 'transferComputer';
      const requestAlreadyAcive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyAcive || payload.override) {
        requestPromise = axios.post(
          'Computers/TransferToCustomer',
          payload,
        ).then(({ data }) => {
          return data;
        });
        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    /* / Computers */

    /* Users */
    getUser({ commit }, payload = {}) {
      const activeRequestID = 'getUser';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Users/GetByID',
          { params: payload.params },
        ).then(({ data }) => {
          commit('setActiveUser', data);
          return axios.get(
            'Users/GetAssignedPermissionsByUserID',
            { params: payload.params },
          );
        }).then(({ data }) => commit('setActiveUserPermissions', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getUsers({ state, commit }, payload = {}) {
      const activeRequestID = 'getUsers';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Users/GetAll',
          { params: state.userSearchParameters },
        ).then(({ data }) => commit('setUsers', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getAllUsers({ commit }, payload = {}) {
      const activeRequestID = 'getAllUsers';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Users/GetAll',
        ).then(({ data }) => commit('setAllUsers', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getRoles({ commit }, payload = {}) {
      const activeRequestID = 'getRoles';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Users/GetAllRoles',
        ).then(({ data }) => commit('setRoles', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getAvailablePermissions({ commit }, payload = {}) {
      const activeRequestID = 'getAvailablePermissions';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'Users/GetAllPermissions',
        ).then(({ data }) => commit('setAvailablePermissions', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    createUser({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'createUser';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Users/Create',
          state.activeUser,
        ).then(({ data }) => {
          if (payload.resetToUser) {
            commit('setActiveUser', data);
          }

          return dispatch('getUsers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    updateUser({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'updateUser';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        const userUpdatePromise = axios.post(
          'Users/Update',
          state.activeUser,
        ).then(returnData);

        const permissionsData = {
          UserID: state.activeUser.UserID,
          PermissionCodes: _.map(
            state.activeUserPermissions,
            'PermissionCode',
          ),
        };

        const permissionsUpdatePromise = axios.post(
          'Users/UpdateAssignedPermissions',
          permissionsData,
        ).then(returnData);

        requestPromise = Promise.all([
          userUpdatePromise,
          permissionsUpdatePromise,
        ]).then((data) => {
          if (payload.resetToUser) {
            const [user] = data;
            commit('setActiveUser', user);
          } else {
            commit('setActiveUserPermissions');
          }

          return dispatch('getUsers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    deleteUser({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'deleteUser';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Users/Delete',
          state.activeUser,
        ).then(() => dispatch('getUsers'))
          .then(() => commit('setActiveUser'));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    restoreUser({ state, commit, dispatch }, payload = {}) {
      const activeRequestID = 'restoreUser';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Users/Restore',
          state.activeUser,
        ).then(() => dispatch('getUsers'))
          .then(() => commit('setActiveUser'));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    /* / Users */

    /* Support Contracts */
    getSupportContract({ commit }, payload = {}) {
      const activeRequestID = 'getSupportContract';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'SupportContracts/GetByID',
          { params: payload.params },
        ).then(({ data }) => commit('setActiveSupportContract', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getSupportContracts({ commit }, payload = {}) {
      const activeRequestID = 'getSupportContracts';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'SupportContracts/GetAll',
        ).then(({ data }) => commit('setSupportContracts', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getComputerSupportContracts({ commit }, payload = {}) {
      const activeRequestID = 'getComputerSupportContracts';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'SupportContracts/GetAll',
          { params: payload.params },
        ).then(({ data }) => {
          commit('setComputerSupportContracts', data);
          return data;
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    createSupportContract({ state, commit }, payload = {}) {
      const activeRequestID = 'createSupportContract';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'SupportContracts/Create',
          state.activeSupportContract,
        ).then(() => {
          EventBus.$emit('getComputerSupportContracts');
          EventBus.$emit('getCustomerComputers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    updateSupportContract({ state, commit }, payload = {}) {
      const activeRequestID = 'updateSupportContract';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'SupportContracts/Update',
          state.activeSupportContract,
        ).then(() => {
          EventBus.$emit('getComputerSupportContracts');
          EventBus.$emit('getCustomerComputers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    updateAllSupportContracts({ state, commit }, payload = {}) {
      const activeRequestID = 'updateAllSupportContracts';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      const data = {
        CustomerID: state.activeCustomer.CustomerID,
        SupportContractEndUtc: payload.supportContractEndUtc,
        SupportContractNotes: '',
      };

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'SupportContracts/UpdateAllContracts',
          data,
        ).then(() => {
          EventBus.$emit('getCustomerComputers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    deleteSupportContract({ state, commit }, payload = {}) {
      const activeRequestID = 'deleteSupportContract';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'SupportContracts/Delete',
          state.activeSupportContract,
        ).then(() => {
          EventBus.$emit('getComputerSupportContracts');
          EventBus.$emit('getCustomerComputers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    restoreSupportContract({ state, commit }, payload = {}) {
      const activeRequestID = 'restoreSupportContract';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'SupportContracts/Restore',
          state.activeSupportContract,
        ).then(() => {
          EventBus.$emit('getComputerSupportContracts');
          EventBus.$emit('getCustomerComputers');
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    openSupportContractModalForm({ commit, dispatch }, payload = {}) {
      const newSupportContract = _.merge(
        {},
        defaultSupportContract,
        payload.supportContract,
      );

      let supportContractPromise = Promise.resolve(newSupportContract);

      if (payload.supportContract && payload.supportContract.SupportContractID) {
        supportContractPromise = dispatch('getSupportContract', {
          params: { SupportContractID: payload.supportContract.SupportContractID },
        });
      } else {
        supportContractPromise
          .then((newSupportContract) => commit('setActiveSupportContract', newSupportContract));
      }

      return supportContractPromise
        .then(() => commit('setSupportContractModalFormOpen', true));
    },
    /* / Support Contracts */

    /* Codes */
    openCodeModalForm({ state }, payload = {}) {
      const activeCode = { [payload.config.property]: '' };
      state.activeCodeConfig = payload.config;
      state.codeModalFormOpen = true;
      state.activeCode = _.clone(activeCode);
      state.activeCodeCopy = _.clone(activeCode);
    },
    createCode({ state, commit }, payload = {}) {
      const activeRequestID = 'createCode';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          state.activeCodeConfig.createEndpoint,
          state.activeCode,
        ).then(() => axios.get(state.activeCodeConfig.refresh.getEndpoint))
          .then(({ data }) => {
            state[state.activeCodeConfig.refresh.list] = data;
          });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    /* / Codes */

    /* Transaction History */
    getTransactionHistory({ state, commit }, payload = {}) {
      const activeRequestID = 'getTransactionHistory';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'TableHistory/GetAll',
          { params: state.transactionHistorySearchParameters },
        ).then(({ data }) => commit('setTransactionHistory', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    getTransactionHistoryEntities({ commit }, payload = {}) {
      const activeRequestID = 'getTransactionHistoryEntities';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.get(
          'TableHistory/GetEntities',
        ).then(({ data }) => commit('setTransactionHistoryEntities', data));

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    /* / Transaction History */

    /* Auth */
    signIn({ commit }, payload = {}) {
      const activeRequestID = 'signIn';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Users/SignIn',
          payload.credentials,
        ).then(({ data }) => {
          const user = data;

          axios.defaults.headers.common.Authorization = `Bearer ${user.AuthToken}`;
          browserStorage.localStorageSet('token', user.AuthToken);
          commit('authSuccess', user);

          return axios.get(
            'Users/GetAssignedPermissionsByUserID',
            { params: { UserID: user.UserID } },
          );
        }).then(({ data }) => {
          commit('setUserPermissions', data);
        }).catch(() => {
          commit('clearToken');
          return Promise.reject();
        });

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    signOut({ commit }) {
      const promise = axios.post('Users/SignOut');
      commit('signOut');
      return promise;
    },
    forgotPassword({ commit }, payload = {}) {
      const activeRequestID = 'forgotPassword';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Users/SendTempPasswordEmail',
          {
            UserEmailAddress: payload.userEmailAddress,
          },
        );

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    updatePassword({ state, commit }, payload = {}) {
      const activeRequestID = 'updatePassword';
      const requestAlreadyActive = isRequestActive(activeRequestID);
      let requestPromise = Promise.resolve();

      if (!requestAlreadyActive || payload.override) {
        requestPromise = axios.post(
          'Users/UpdatePassword',
          state.activeUserUpdatePassword,
        );

        commit('addActiveRequest', {
          activeRequestID,
          requestPromise,
        });
      }

      return requestPromise;
    },
    /* / Auth */
  },
});

// periodically check active requests
// and remove those that have resolved
const removeResolvedRequests = () => {
  _.each(store.state.activeRequests, (request) => {
    request.requestPromise
      .catch(_.noop)
      .finally(() => {
        const unresolved = _.reject(
          store.state.activeRequests,
          { activeRequestID: request.activeRequestID },
        );

        store.commit('setActiveRequests', unresolved);
      });
  });
};

setInterval(removeResolvedRequests, 500);

export default store;
